import { useState } from "react";
import { Link } from "react-router-dom";
import { Copy, Check, Loader2, Inbox } from "lucide-react";
import { toast } from "sonner";

export function Logo({ size = "md", className = "", noLink = false, variant = "horizontal" }) {
  const h = size === "lg" ? "h-12" : size === "sm" ? "h-8" : "h-10";
  const inner = variant === "full" ? (
    <img src="/brand/logo-full.png" alt="AI TECHNOLOGY Trading" className={`${size === "lg" ? "h-40" : "h-28"} w-auto drop-shadow-[0_0_24px_rgba(0,160,255,0.35)]`} />
  ) : (
    <>
      <img src="/brand/logo-mark.png" alt="" className={`${h} w-auto drop-shadow-[0_0_14px_rgba(0,160,255,0.35)]`} />
      <img src="/brand/logo-wordmark.png" alt="AI TECHNOLOGY Trading" className={`${size === "lg" ? "h-9" : size === "sm" ? "h-6" : "h-7"} w-auto`} />
    </>
  );
  if (noLink) return <div data-testid="brand-logo" className={`flex items-center gap-2.5 ${className}`}>{inner}</div>;
  return (
    <Link to="/" data-testid="brand-logo" className={`flex items-center gap-2.5 ${className}`}>{inner}</Link>
  );
}

export function CopyButton({ value, testId = "copy-btn", label }) {
  const [done, setDone] = useState(false);
  const copy = async () => {
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(value);
      } else {
        const ta = document.createElement("textarea");
        ta.value = value; ta.style.position = "fixed"; ta.style.opacity = "0";
        document.body.appendChild(ta); ta.focus(); ta.select();
        document.execCommand("copy"); document.body.removeChild(ta);
      }
      setDone(true);
      toast.success("Copied to clipboard");
      setTimeout(() => setDone(false), 1500);
    } catch { toast.error("Copy failed"); }
  };
  return (
    <button data-testid={testId} onClick={copy}
      className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-cyan-500/10 border border-cyan-500/30 text-cyan-300 text-sm hover:bg-cyan-500/20 transition-colors">
      {done ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
      {label || (done ? "Copied" : "Copy")}
    </button>
  );
}

export function Loader({ label = "Loading..." }) {
  return (
    <div className="flex flex-col items-center justify-center py-20 text-slate-400" data-testid="loader">
      <Loader2 className="w-8 h-8 animate-spin text-cyan-400 mb-3" />
      <p className="text-sm">{label}</p>
    </div>
  );
}

export function EmptyState({ title = "Nothing here yet", subtitle, icon: Icon = Inbox, action }) {
  return (
    <div className="flex flex-col items-center justify-center py-16 text-center" data-testid="empty-state">
      <div className="w-16 h-16 rounded-2xl glass flex items-center justify-center mb-4">
        <Icon className="w-7 h-7 text-cyan-400/70" />
      </div>
      <h3 className="font-heading text-lg text-slate-200">{title}</h3>
      {subtitle && <p className="text-sm text-slate-500 mt-1 max-w-sm">{subtitle}</p>}
      {action && <div className="mt-5">{action}</div>}
    </div>
  );
}

const statusMap = {
  pending: "bg-amber-500/15 text-amber-300 border-amber-500/30",
  approved: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
  completed: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
  matured: "bg-cyan-500/15 text-cyan-300 border-cyan-500/30",
  credited: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
  active: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
  rejected: "bg-red-500/15 text-red-300 border-red-500/30",
  failed: "bg-red-500/15 text-red-300 border-red-500/30",
  suspended: "bg-red-500/15 text-red-300 border-red-500/30",
  not_submitted: "bg-slate-500/15 text-slate-300 border-slate-500/30",
};
export function StatusBadge({ status }) {
  const cls = statusMap[status] || "bg-slate-500/15 text-slate-300 border-slate-500/30";
  return (
    <span data-testid={`status-${status}`}
      className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium border capitalize ${cls}`}>
      {(status || "").replace("_", " ")}
    </span>
  );
}

export function SectionHeading({ eyebrow, title, subtitle, center }) {
  return (
    <div className={`${center ? "text-center mx-auto" : ""} max-w-2xl mb-12`}>
      {eyebrow && <p className="label-eyebrow text-xs font-mono uppercase tracking-[0.25em] text-cyan-400 mb-3">{eyebrow}</p>}
      <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight text-white">{title}</h2>
      {subtitle && <p className="text-slate-400 mt-4 text-base leading-relaxed">{subtitle}</p>}
    </div>
  );
}

export const fmt = (n) => Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
export const fmtDate = (d) => d ? new Date(d).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" }) : "-";
export const fmtDateTime = (d) => d ? new Date(d).toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }) : "-";
